/* eslint-disable no-nested-ternary */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modifier, Variant, VariantModifier, VariantModifierPriceUpdateRequest } from '@/types/menus.types';
import { useSTKContext } from '@/context/context';
import { StoreGroupType } from '@/context/context.types';

export const useMenuModifiersGroup = (
  enabled: boolean,
  stores: string[],
  storeGroups: StoreGroupType[],
  categoryName: string,
) => {
  const { apiClient } = useSTKContext();

  const reqBody = { stores, storeGroups: storeGroups.flatMap((st) => st.storeGroupId), modifierCategory: categoryName };
  return useQuery<Modifier[]>({
    queryKey: ['store-grouped-modifiers', { ...stores, categoryName }],
    queryFn: async () => {
      const response = await apiClient.put<Modifier[]>(`/modifiers`, reqBody);
      return response.data;
    },
    enabled,
  });
};

export const useVariant = (enabled: boolean, stores: string[], storeGroups: StoreGroupType[], variantId: string) => {
  const { apiClient } = useSTKContext();
  const reqBody = { stores, storeGroups: storeGroups.flatMap((st) => st.storeGroupId), variantIds: [variantId] };
  return useQuery<Variant>({
    queryKey: ['variant', { ...stores, variantId }],
    queryFn: async () => {
      const response = await apiClient.put<Variant[]>(`/modifiers/variants`, reqBody);
      return response.data[0];
    },
    enabled,
  });
};

export const useVariants = (
  enabled: boolean,
  stores: string[],
  storeGroups: StoreGroupType[],
  modifierKeys: {
    variantId: string | undefined;
    modifierId: string;
    modifierWeightId: string;
    slotId: string;
  }[],
  categoryName: string,
) => {
  const { apiClient } = useSTKContext();
  const reqBody = {
    stores,
    storeGroups: storeGroups.flatMap((st) => st.storeGroupId),
    modifierKeys,
    modifierCategory: categoryName,
  };
  return useQuery<VariantModifier[]>({
    queryKey: ['variantModifier', { ...stores, items: modifierKeys }],
    queryFn: async () => {
      const response = await apiClient.put<VariantModifier[]>(`/modifiers/variants`, reqBody);
      return response.data;
    },
    enabled,
  });
};

export const useUpdateProductPrice = () => {
  const { apiClient } = useSTKContext();
  return useMutation(async (requestBody: VariantModifierPriceUpdateRequest[]) => {
    const response = await apiClient.put('/modifiers/update-variant-price', requestBody);
    return response.data;
  });
};

export const useModifierPriceDeviations = (
  enabled: boolean,
  modifierCategory: string,
  stores: string[],
  modifierKeys: {
    modifierId: string;
    slotId: string;
    modifierWeightId: string;
    variantId: string;
  }[],
) => {
  const { apiClient } = useSTKContext();
  console.log({ modifierKeys }, 'inside useModifierPriceDeviations modifier.service.ts');
  const reqBody = {
    modifierKeys: Array.isArray(modifierKeys)
      ? typeof modifierKeys[0] === 'string'
        ? [
            {
              modifierId: modifierKeys[0] || '',
              slotId: modifierKeys[1] || '',
              modifierWeightId: modifierKeys[2] || '',
              variantId: modifierKeys[3] || '',
            },
          ]
        : modifierKeys.map((key) => ({
            modifierId: key.modifierId || '',
            slotId: key.slotId || '',
            modifierWeightId: key.modifierWeightId || '',
            variantId: key.variantId || '',
          }))
      : [],
    stores,
    modifierCategory,
  };
  return useQuery({
    queryKey: ['modifierPriceDeviations', reqBody],
    queryFn: async () => {
      const { data } = await apiClient.put(`/modifiers/price-deviations`, reqBody);
      return data;
    },
    enabled,
  });
};
